import * as React from 'react';
import { MapPin, Plus } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Autocomplete } from '@/components/forms/Autocomplete';
import { Button } from '@/components/forms/Button';
import { Input } from '@/components/forms/Input';
import { Select } from '@/components/forms/Select';
import { InlineActivityForm } from '@/components/common/InlineActivityForm';
import { InlineActivityTypeForm } from '@/components/common/InlineActivityTypeForm';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { getActivityTypes } from '@/services/activity-types.service';
import {
  appendFallbackEstablishmentOption,
  buildClassroomSelectOptions,
  EMPTY_ACTIVITY_DRAFT,
  EMPTY_ACTIVITY_TYPE_DRAFT,
  toIdLabelOptions,
  withEmptySelectOption,
} from '@/components/sessions/inperson/session-form-utils';
import { useActorEstablishmentAssignmentScope } from '@/hooks/useActorEstablishmentAssignmentScope';
import { useEnrichEstablishmentLabels } from '@/hooks/useEnrichEstablishmentLabels';
import { formatEstablishmentSelectLabel } from '@/utils/format-establishment-select-label-utils';
import type { UseFormValidationReturn } from '@/hooks/useFormValidation';
import type { Activity, CreateActivityDto } from '@/types/activity.types';
import type { CreateActivityTypeDto } from '@/types/activity-type.types';
import type { Establishment, Classroom } from '@/types/establishment.types';
import type { Program } from '@/types/program.types';
import type {
  SessionFormData,
  SessionFormFieldChange,
  SessionFormFieldValidation,
} from '@/types/inperson/session.types';

/** Props for {@link TutoringSessionEstablishmentField}. */
interface EstablishmentFieldProps {
  establishmentId: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  state?: 'default' | 'error' | 'success';
  error?: string;
  fallbackEstablishment?: Establishment | null;
  fallbackEstablishments?: Establishment[];
  inputSize?: 'sm' | 'md';
}

/** Establishment autocomplete used by the tutoring location section. */
function TutoringSessionEstablishmentField({
  establishmentId,
  onChange,
  onBlur,
  state,
  error,
  fallbackEstablishment,
  fallbackEstablishments,
  inputSize,
}: Readonly<EstablishmentFieldProps>) {
  const {
    selectOptions,
    isSingleLocked,
    singleEstablishment,
    singleEstablishmentLabel,
    isLoading: establishmentScopeLoading,
    isSearchLoading,
    isReady: establishmentScopeReady,
    onSearch,
    helperText,
  } = useActorEstablishmentAssignmentScope();

  const labelCacheRef = React.useRef<Map<string, string>>(new Map());

  const primeLabelCache = React.useCallback(
    (
      entries: Array<{
        id: string;
        name: string;
        city?: string | null;
        postalCode?: string | null;
        department?: string | null;
        country?: string | null;
      }>,
    ) => {
      for (const entry of entries) {
        if (!entry.id || !entry.name?.trim()) continue;
        labelCacheRef.current.set(
          entry.id,
          formatEstablishmentSelectLabel({ ...entry, name: entry.name }),
        );
      }
    },
    [],
  );

  useEnrichEstablishmentLabels(establishmentId ? [establishmentId] : [], primeLabelCache);

  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;

  React.useEffect(() => {
    if (!isSingleLocked || !singleEstablishment) return;
    if (establishmentId === singleEstablishment.id) return;
    onChangeRef.current(singleEstablishment.id);
  }, [isSingleLocked, singleEstablishment, establishmentId]);

  React.useEffect(() => {
    if (!fallbackEstablishment && !fallbackEstablishments?.length) return;
    const entries = [
      ...(fallbackEstablishment ? [fallbackEstablishment] : []),
      ...(fallbackEstablishments ?? []),
    ];
    primeLabelCache(
      entries.map((establishment) => ({
        id: establishment.id,
        name: establishment.name,
        city: establishment.city,
        postalCode: establishment.postalCode,
        department: establishment.department,
        country: establishment.country,
      })),
    );
  }, [fallbackEstablishment, fallbackEstablishments, primeLabelCache]);

  if (establishmentScopeLoading) {
    return (
      <Input
        label="Établissement"
        value=""
        placeholder="Chargement des établissements..."
        disabled
        required
        inputSize={inputSize}
      />
    );
  }

  if (isSingleLocked && singleEstablishmentLabel) {
    return (
      <Input
        label="Établissement"
        value={singleEstablishmentLabel}
        disabled
        required
        helperText="Établissement assigné à votre compte"
        state={state}
        error={error}
        inputSize={inputSize}
      />
    );
  }

  if (!establishmentScopeReady) {
    return null;
  }

  const options = appendFallbackEstablishmentOption(
    selectOptions,
    establishmentId,
    fallbackEstablishment,
    fallbackEstablishments,
  );

  return (
    <Autocomplete
      label="Établissement"
      placeholder="Rechercher parmi vos établissements..."
      options={options}
      value={establishmentId}
      onChange={onChange}
      onBlur={onBlur}
      onSearch={onSearch}
      isLoading={isSearchLoading}
      minSearchLength={0}
      helperText={helperText}
      required
      state={state}
      error={error}
      inputSize={inputSize}
    />
  );
}

/** Props for {@link TutoringSessionLocationSection}. */
interface Props {
  form: SessionFormData;
  onFieldChange: SessionFormFieldChange;
  onBlur: (field: string) => void;
  validation: SessionFormFieldValidation;
  establishments: Establishment[];
  classrooms: Classroom[];
  programs: Program[];
  activities: Activity[];
  selectedEstablishment?: Establishment | null;
  showActivityForm: boolean;
  setShowActivityForm: (show: boolean) => void;
  showActivityTypeForm: boolean;
  setShowActivityTypeForm: (show: boolean) => void;
  newActivityData: Partial<CreateActivityDto>;
  setNewActivityData: React.Dispatch<React.SetStateAction<Partial<CreateActivityDto>>>;
  newActivityTypeData: Partial<CreateActivityTypeDto>;
  setNewActivityTypeData: React.Dispatch<React.SetStateAction<Partial<CreateActivityTypeDto>>>;
  activityValidation: UseFormValidationReturn;
  activityTypeValidation: UseFormValidationReturn;
}

/** Establishment / room location for a tutoring session. */
export function TutoringSessionLocationSection({
  form,
  onFieldChange,
  onBlur,
  validation,
  establishments,
  classrooms,
  programs,
  activities,
  selectedEstablishment,
  showActivityForm,
  setShowActivityForm,
  showActivityTypeForm,
  setShowActivityTypeForm,
  newActivityData,
  setNewActivityData,
  newActivityTypeData,
  setNewActivityTypeData,
  activityValidation,
  activityTypeValidation,
}: Readonly<Props>) {
  const { data: activityTypes = [] } = useQuery({
    queryKey: ['activityTypes'],
    queryFn: async () => (await getActivityTypes()).data,
  });

  const classroomOptions = buildClassroomSelectOptions(classrooms, form.establishmentId);
  const programOptions = withEmptySelectOption(
    'Sélectionner un programme',
    toIdLabelOptions(programs, (p) => p.name),
  );
  const activityOptions = withEmptySelectOption(
    'Sélectionner une activité',
    toIdLabelOptions(activities, (a) => a.label),
  );
  const activityTypeOptions = withEmptySelectOption(
    'Sélectionner un type',
    toIdLabelOptions(activityTypes, (at) => at.label),
  );

  function handleEstablishmentChange(value: string): void {
    onFieldChange('establishmentId', value);
  }
  function handleEstablishmentBlur(): void {
    onBlur('establishmentId');
  }
  function handleRoomChange(value: string): void {
    onFieldChange('roomId', value);
  }
  function handleRoomBlur(): void {
    onBlur('roomId');
  }
  function handleProgramChange(value: string): void {
    onFieldChange('programId', value);
  }
  function handleProgramBlur(): void {
    onBlur('programId');
  }
  function handleActivityChange(value: string): void {
    onFieldChange('activityId', value);
  }
  function handleActivityBlur(): void {
    onBlur('activityId');
  }
  function handleOpenActivityForm(): void {
    setShowActivityForm(true);
  }
  function handleCloseActivityForm(): void {
    setNewActivityData({ ...EMPTY_ACTIVITY_DRAFT });
    setNewActivityTypeData({ ...EMPTY_ACTIVITY_TYPE_DRAFT });
    setShowActivityTypeForm(false);
    setShowActivityForm(false);
    onFieldChange('activityId', '');
  }
  function handleValidateActivity(): void {
    const activityValid = activityValidation.validateForm(newActivityData);
    const activityTypeValid = showActivityTypeForm
      ? activityTypeValidation.validateForm(newActivityTypeData)
      : true;
    if (!activityValid || !activityTypeValid) {
      toast.error(getErrorMessage('requiredFields'));
      return;
    }
    setShowActivityForm(false);
    toast.success('Activité prête à être créée avec la séance');
  }
  function handleCloseActivityTypeForm(): void {
    setNewActivityTypeData({ ...EMPTY_ACTIVITY_TYPE_DRAFT });
    setShowActivityTypeForm(false);
  }
  function handleValidateActivityType(): void {
    const activityTypeValid = activityTypeValidation.validateForm(newActivityTypeData);
    if (!activityTypeValid) {
      toast.error(getErrorMessage('requiredActivityType'));
      return;
    }
    toast.success("Type d'activité prêt à être créé avec la séance");
  }

  function renderActivityTypeForm(): React.ReactNode {
    if (!showActivityTypeForm) return null;
    return (
      <div className="ml-6 border-l border-[var(--color-2-nuance-1)] pl-4">
        <InlineActivityTypeForm
          newActivityTypeData={newActivityTypeData}
          setNewActivityTypeData={setNewActivityTypeData}
          activityTypeValidation={activityTypeValidation}
          onClose={handleCloseActivityTypeForm}
          onValidate={handleValidateActivityType}
        />
      </div>
    );
  }

  function renderActivityField(): React.ReactNode {
    if (showActivityForm) {
      return (
        <div className="col-span-2 space-y-4">
          <InlineActivityForm
            newActivityData={newActivityData}
            setNewActivityData={setNewActivityData}
            showActivityTypeForm={showActivityTypeForm}
            setShowActivityTypeForm={setShowActivityTypeForm}
            activityTypeOptions={activityTypeOptions}
            activityValidation={activityValidation}
            onClose={handleCloseActivityForm}
            onValidate={handleValidateActivity}
          />
          {renderActivityTypeForm()}
        </div>
      );
    }
    return (
      <div className="space-y-2">
        <Select
          label="Activité"
          placeholder="Sélectionner une activité"
          options={activityOptions}
          value={form.activityId}
          onChange={handleActivityChange}
          onBlur={handleActivityBlur}
          state={validation.getFieldState('activityId')}
          error={validation.getFieldError('activityId')}
          size="sm"
        />
        <Button
          variant="outline"
          size="sm"
          icon={Plus}
          iconPosition="left"
          onClick={handleOpenActivityForm}
        >
          Créer une nouvelle activité
        </Button>
      </div>
    );
  }

  return (
    <div className="form-section">
      <h4 className="form-section-title">
        <MapPin className="w-4 h-4" />
        Lieu et contexte
      </h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TutoringSessionEstablishmentField
          establishmentId={form.establishmentId}
          onChange={handleEstablishmentChange}
          onBlur={handleEstablishmentBlur}
          state={validation.getFieldState('establishmentId')}
          error={validation.getFieldError('establishmentId')}
          fallbackEstablishment={selectedEstablishment}
          fallbackEstablishments={establishments}
          inputSize="sm"
        />
        <Select
          label="Salle"
          placeholder="Sélectionner une salle"
          options={classroomOptions}
          value={form.roomId}
          onChange={handleRoomChange}
          onBlur={handleRoomBlur}
          state={validation.getFieldState('roomId')}
          error={validation.getFieldError('roomId')}
          size="sm"
          disabled={!form.establishmentId}
        />
        <Select
          label="Programme"
          placeholder="Sélectionner un programme"
          options={programOptions}
          value={form.programId}
          onChange={handleProgramChange}
          onBlur={handleProgramBlur}
          state={validation.getFieldState('programId')}
          error={validation.getFieldError('programId')}
          size="sm"
        />
        {renderActivityField()}
      </div>
    </div>
  );
}
